/**
 * The most repeated component in the product: one game, two team lines and
 * the server's status, in the anatomy of the Scores UX spec v1.0 section 1.
 *
 * ONE ANATOMY AT EVERY WIDTH. The row root is a horizontal flex box with
 * exactly two layout children, the teams block and the status column, so the
 * status can never fall below the row and read as the header of the next one
 * (the bug in the owner's 2026-10-19 screenshots). There is no band and no
 * live rail.
 *
 *   [teams block, flex 1]  |hairline|  [status column, 88 or 152] [bell, >=640]
 *     away: [mark 28] 8 [name][star] 8 [score 36]
 *     home: [mark 28] 8 [name][star] 8 [score 36]
 *
 * Away on top, home below. Each score sits on its own team line, so the two
 * scores stack in one right aligned column and never render as "10 14".
 * Scheduled, postponed and canceled rows have no score cell at all and the
 * name takes its room.
 *
 * THE NAME NEVER ELLIPSIZES. It is one line and shows the longest server name
 * that fits the measured cell (`pickTeamLabel` in `@sportsmet/theme/scores`):
 * "Seattle Seahawks", then the server's `shortName` when it sends one, then
 * the abbreviation. The cell is measured with a ResizeObserver; the first
 * render uses the shared phone arithmetic (`scoreRowGeometry.ts`) for the
 * current viewport so the label does not flash. The accessible name is always
 * the full name.
 *
 * A hockey team on a power play gets the server's "PP" badge inline after its
 * name, before the star (ISSUE-1393, v1 `power_play_badge.dart`).
 *
 * A ranked college team gets the server's poll rank ("5") in small muted
 * tabular digits just before its name, the way ESPN draws it (ISSUE-1388).
 * Its width comes out of the name's room, so the name falls back to the
 * abbreviation at the same widths as the phone. It is read as "No. 5".
 *
 * A followed team gets a small filled star INLINE after its name, in normal
 * flow, so it can never sit on the mark. The row has no follow control
 * (ISSUE-1081): following lives on the team page and the game header.
 *
 * WHERE A TAP GOES. The whole row is one press target that opens the game: an
 * empty `absolute inset-0` button, the row's last child, the only positioned
 * element in the row. There are no team targets inside a row; team pages are
 * reached from the game header and standings. The bell (ISSUE-979, 640px and
 * up only) carries `relative z-10` to sit above it.
 *
 * Every box is a pixel token, so the row is one size at every text scale
 * (ISSUE-908); the arithmetic lives in `packages/theme/src/scores/
 * scoreRowGeometry.ts` and is asserted in `packages/theme/src/tokens.test.ts`.
 */
import { tokens } from "@sportsmet/theme";
import {
  pickTeamLabel,
  SCORE_ROW_FAVORITE_STAR_PX,
  SCORE_ROW_POSSESSION_DOT_PX,
  SCORE_ROW_POWER_PLAY_BADGE_PX,
  SCORE_ROW_SCORE_CELL_PX,
  scoreRowNameCellEstimatePx,
  scoreRowPollRankBadgePx,
} from "@sportsmet/theme/scores";
import { cn } from "@sportsmet/ui";
import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { FavoriteStarIcon } from "../../icons/glyphs";
import { LiveStateRowGlyph } from "../liveState/LiveStateRowGlyph";
import { PowerPlayBadge } from "../liveState/PowerPlayBadge";
import { ScoreRowActions } from "./ScoreRowActions";
import { TeamDisc } from "./TeamDisc";
import {
  toScoreRowModel,
  type typeScoreRowModel,
  type typeScoreRowStatusTone,
  type typeScoresEventInput,
  type typeScoreTeamLine,
} from "./scoreRowModel";

export type typeScoreRowProps = {
  event: typeScoresEventInput;
  /** Opens game detail. When passed, the whole row is one button. When
   *  undefined there is no game target and the row is inert. */
  onOpenGame?: ((eventId: string) => void) | undefined;
  /** The fan's followed teams: a followed team gets the inline star and the
   *  row label gains ", your team". */
  favoriteTeamIds?: readonly string[] | undefined;
  /** A caption above the away line, the league's short name, for a card that
   *  mixes leagues ("Your teams"). */
  leagueCaption?: string | undefined;
};

/** The name font size the label estimate assumes: `text.body`. */
const NAME_FONT_PX = tokens.text.body.size;

const SCORE_CELL_STYLE: CSSProperties = { width: SCORE_ROW_SCORE_CELL_PX };

// The full row button. Its focus ring is drawn INSIDE the row, because the
// card around the row clips, so an outside ring would be cut on its edges.
const INSET_FOCUS_TARGET_CLASS =
  "absolute inset-0 cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus-ring";

/** What a name cell of `cellPx` leaves for the label on this line. */
function labelRoomPx(line: typeScoreTeamLine, cellPx: number): number {
  return (
    cellPx -
    scoreRowPollRankBadgePx(line.pollRankLabel) -
    (line.isFavorite ? SCORE_ROW_FAVORITE_STAR_PX : 0) -
    (line.hasPossession ? SCORE_ROW_POSSESSION_DOT_PX : 0) -
    (line.powerPlayLabel !== null ? SCORE_ROW_POWER_PLAY_BADGE_PX : 0)
  );
}

/** The label each line shows in a name cell of `cellPx`. Exported so the
 *  choice is testable without a layout engine. */
export function teamLabelsFor(
  model: typeScoreRowModel,
  cellPx: number,
): { away: string; home: string } {
  const label = (line: typeScoreTeamLine): string =>
    pickTeamLabel(
      {
        name: line.name,
        shortName: line.shortName,
        abbreviation: line.abbreviation,
      },
      labelRoomPx(line, cellPx),
      NAME_FONT_PX,
    );
  return { away: label(model.away), home: label(model.home) };
}

function initialCellPx(hasScore: boolean): number {
  const viewport =
    typeof window === "undefined"
      ? tokens.size.contentWidth
      : window.innerWidth;
  return scoreRowNameCellEstimatePx({
    layoutViewportPx: viewport,
    hasScore,
    hasBellWhenWide: true,
  });
}

/** The measured width of the away name cell (both lines are the same
 *  width), starting from the shared arithmetic and updated on resize. */
function useNameCellPx(
  ref: RefObject<HTMLSpanElement | null>,
  hasScore: boolean,
): number {
  const [cellPx, setCellPx] = useState(() => initialCellPx(hasScore));
  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null || typeof ResizeObserver === "undefined") return;
    const measure = (): void => {
      const width = element.getBoundingClientRect().width;
      // A cell not laid out yet (display none, a detached test tree) keeps
      // the estimate rather than collapsing every name to its abbreviation.
      if (width > 0) setCellPx(width);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [ref, hasScore]);
  return cellPx;
}

/** The 28px round team mark: the logo on the light logo disc, or the
 *  initials plate. Never a pill, never badged (section 1.2). */
function TeamMarkDisc(props: { line: typeScoreTeamLine }): ReactNode {
  const { line } = props;
  return (
    <TeamDisc
      plateText={line.plateText}
      logoUrl={line.logoUrl}
      plateColorHex={line.plateColorHex}
      size="row"
      testId="score-row-mark"
      imageTestId="score-row-mark-image"
    />
  );
}

/** ISSUE-1388. The poll rank before a ranked team's name: small, muted,
 *  tabular digits with no "#". Screen readers hear "No. 5". */
export function PollRankBadge(props: { label: string }): ReactNode {
  return (
    <span
      className="shrink-0 whitespace-nowrap text-small tabular-nums text-ink-secondary"
      data-testid="poll-rank"
    >
      <span className="sr-only">No. </span>
      {props.label}
    </span>
  );
}

function TeamLine(props: {
  line: typeScoreTeamLine;
  label: string;
  hasScore: boolean;
  testId: string;
  cellRef?: RefObject<HTMLSpanElement | null>;
}): ReactNode {
  const { line } = props;
  const isLoser = line.emphasis === "lose";
  return (
    <div
      className="flex h-score-mark-size min-w-0 items-center gap-score-row-gap"
      data-testid={props.testId}
      data-emphasis={line.emphasis}
    >
      <TeamMarkDisc line={line} />
      <span
        ref={props.cellRef}
        className="flex min-w-0 flex-1 items-center gap-score-live-gap"
        data-testid="score-row-team-name"
      >
        {line.pollRankLabel !== null ? (
          <PollRankBadge label={line.pollRankLabel} />
        ) : null}
        <span
          className={cn(
            "min-w-0 overflow-hidden whitespace-nowrap text-body",
            line.emphasis === "win" ? "font-bold" : undefined,
            isLoser ? "text-ink-secondary" : "text-ink",
          )}
          data-testid="score-row-team-name-text"
          data-full-name={line.name}
        >
          {props.label}
        </span>
        {line.hasPossession ? (
          <span
            className="h-score-live-dot w-score-live-dot shrink-0 rounded-full bg-primary-solid"
            data-testid="possession-dot"
            aria-label="has the ball"
            role="img"
          />
        ) : null}
        {line.powerPlayLabel !== null ? (
          <PowerPlayBadge label={line.powerPlayLabel} />
        ) : null}
        {line.isFavorite ? (
          <FavoriteStarIcon
            className="text-primary"
            testId="score-row-favorite-star"
          />
        ) : null}
      </span>
      {props.hasScore ? (
        <span
          className={cn(
            "shrink-0 whitespace-nowrap text-right text-score tabular-nums",
            isLoser ? "text-ink-secondary" : "text-ink",
          )}
          style={SCORE_CELL_STYLE}
          data-testid="score-row-score"
        >
          {line.scoreLabel}
        </span>
      ) : null}
    </div>
  );
}

const STATUS_TONE_CLASS: Record<typeScoreRowStatusTone, string> = {
  live: "text-error",
  delayed: "text-warning",
  final: "text-ink",
  muted: "text-ink-secondary",
};

function StatusLabel(props: { model: typeScoreRowModel }): ReactNode {
  const { model } = props;
  const dotClass =
    model.statusTone === "live"
      ? "bg-error-solid"
      : model.statusTone === "delayed"
        ? "bg-warning-solid"
        : null;
  return (
    <span
      className={cn(
        "whitespace-normal break-normal",
        STATUS_TONE_CLASS[model.statusTone],
      )}
      data-testid="score-row-state-label"
      data-tone={model.statusTone}
    >
      {dotClass !== null ? (
        <span
          className={cn(
            "me-score-live-gap inline-block h-score-live-dot w-score-live-dot rounded-full align-middle",
            dotClass,
          )}
          data-testid="score-row-live-dot"
          aria-hidden="true"
        />
      ) : null}
      {model.stateLabel}
    </span>
  );
}

export const ScoreRow = (props: typeScoreRowProps): ReactNode => {
  const model = toScoreRowModel(props.event, {
    favoriteTeamIds: props.favoriteTeamIds,
  });
  const cellRef = useRef<HTMLSpanElement>(null);
  const cellPx = useNameCellPx(cellRef, model.hasScore);
  const labels = teamLabelsFor(model, cellPx);

  return (
    <div
      className={cn(
        "relative isolate flex min-h-score-row-min-height flex-row items-center border-t border-hairline px-score-row-inset py-2",
        props.onOpenGame !== undefined
          ? "transition-colors duration-200 hover:bg-surface-sunken motion-reduce:transition-none"
          : undefined,
      )}
      data-testid="score-row"
      data-event-id={model.eventId}
      data-live={model.isLive ? "true" : undefined}
      data-in-play={model.isInPlay ? "true" : undefined}
    >
      <div
        className="flex min-w-0 flex-1 flex-col gap-score-live-gap pe-score-row-inset"
        data-testid="score-row-teams"
      >
        {props.leagueCaption !== undefined && props.leagueCaption !== "" ? (
          <span
            className="text-small text-ink-tertiary"
            data-testid="score-row-league-caption"
          >
            {props.leagueCaption}
          </span>
        ) : null}
        <TeamLine
          line={model.away}
          label={labels.away}
          hasScore={model.hasScore}
          testId="score-row-team-away"
          cellRef={cellRef}
        />
        <TeamLine
          line={model.home}
          label={labels.home}
          hasScore={model.hasScore}
          testId="score-row-team-home"
        />
        {model.startingPitcherLines !== null ? (
          <div
            className="flex min-w-0 flex-col"
            data-testid="score-row-starting-pitchers"
          >
            <span className="sr-only">Starting pitchers: </span>
            {model.startingPitcherLines.map((line) => (
              <span
                key={line}
                className="truncate text-small text-ink-secondary"
                data-testid="score-row-starting-pitcher"
              >
                {line}
              </span>
            ))}
          </div>
        ) : null}
        {model.pregameLine !== null ? (
          <span className="flex min-w-0" data-testid="score-row-pregame-line">
            <span className="sr-only">{model.pregameLine.spokenLabel}</span>
            <span
              aria-hidden="true"
              className="truncate text-small tabular-nums text-ink-secondary"
            >
              {model.pregameLine.label}
            </span>
          </span>
        ) : null}
      </div>
      <div
        className="flex shrink-0 items-center self-stretch border-s border-hairline ps-score-row-inset"
        data-testid="score-row-status"
      >
        <div
          className="flex w-score-status-compact-width flex-col justify-center text-caption sm:w-score-status-width"
          data-testid="score-row-state"
        >
          <StatusLabel model={model} />
          {model.liveState !== null ? (
            <LiveStateRowGlyph
              liveState={model.liveState}
              awayTeamId={props.event.away.teamId}
              homeTeamId={props.event.home.teamId}
            />
          ) : null}
        </div>
        <ScoreRowActions event={props.event} />
      </div>
      {props.onOpenGame !== undefined ? (
        <button
          type="button"
          data-testid="score-row-open-game"
          aria-label={model.ariaLabel}
          className={INSET_FOCUS_TARGET_CLASS}
          onClick={() => props.onOpenGame?.(model.eventId)}
        />
      ) : null}
    </div>
  );
};

/** The loading row: the same anatomy in skeleton bars (section 1.9). Static
 *  under reduced motion. Decorative; the card's status label names it. */
export const ScoreRowSkeleton = (): ReactNode => {
  const bar = "bg-skeleton-base animate-skeleton motion-reduce:animate-none";
  const line = (
    <div className="flex h-score-mark-size items-center gap-score-row-gap">
      <span
        className={cn(
          "h-score-mark-size w-score-mark-size shrink-0 rounded-full",
          bar,
        )}
      />
      <span className="flex min-w-0 flex-1">
        <span className={cn("h-4 w-3/5 rounded-sm", bar)} />
      </span>
      <span
        className={cn("h-4 shrink-0 rounded-sm", bar)}
        style={SCORE_CELL_STYLE}
      />
    </div>
  );
  return (
    <div
      className="flex min-h-score-row-min-height flex-row items-center border-t border-hairline px-score-row-inset py-2"
      data-testid="score-row-skeleton"
      aria-hidden="true"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-score-live-gap pe-score-row-inset">
        {line}
        {line}
      </div>
      <div className="flex shrink-0 items-center self-stretch border-s border-hairline ps-score-row-inset">
        <span
          className={cn(
            "h-4 w-score-status-compact-width rounded-sm sm:w-score-status-width",
            bar,
          )}
        />
      </div>
    </div>
  );
};
